import * as THREE from 'three';
import { sceneUnitsFromMm } from '../../utils/scaler';
import { useModelPropertiesStore } from '../../store/modelPropertiesStore/modelPropertiesStore';
import { useViewerStateStore } from '../../store/viewerStateStore';
import { StageTypeEnum } from '../../store/viewerState/ViewerMode/ViewerMode';

export default class PlaneGridHelper {
    #gridHelper: THREE.GridHelper;
    #enabled: boolean = false;
    #rotationState: number = 0; // 0 = no offset, 1 = 90° Y, 2 = 90° Z
    #lastNormal: THREE.Vector3 | null = null;
    #lastPoint: THREE.Vector3 | null = null;
    #animationFrameId: number | null = null;
    #shaderMaterial!: THREE.ShaderMaterial;
    #circleHelper!: THREE.LineLoop;

    #lockedColor1 = new THREE.Color(0x00E5FF); // Bright neon blue – Y-offset
    #lockedColor2 = new THREE.Color(0x0088AA); // Deep blue – Y-offset
    #zOffsetColor1 = new THREE.Color(0xFF8C00); // Bright orange – Z-offset
    #zOffsetColor2 = new THREE.Color(0xCC6600); // Deep orange – Z-offset
    #unlockedColor1 = new THREE.Color(0x00FF00); // Bright green
    #unlockedColor2 = new THREE.Color(0x00A000); // Deep green

    constructor() {
        const size = sceneUnitsFromMm(1000); // 1 meter grid
        const divisions = 10; // 10cm per division
        
        this.#gridHelper = new THREE.GridHelper(size, divisions, this.#unlockedColor1, this.#unlockedColor2);
        
        // Dispose of the default LineBasicMaterial
        (this.#gridHelper.material as THREE.Material).dispose();

        // Create a custom ShaderMaterial to achieve the radial fade effect
        const shaderMaterial = new THREE.ShaderMaterial({
            vertexColors: true,
            transparent: true,
            depthTest: true,
            // Standard alpha blending for a bright colored object
            blending: THREE.NormalBlending,
            uniforms: {
                size: { value: size },
                color1: { value: this.#unlockedColor1 },
                color2: { value: this.#unlockedColor2 },
                time: { value: 0 },
                pulseSpeed: { value: 4.0 } // Current speed for unlocked
            },
            vertexShader: `
                #ifndef USE_COLOR
                attribute vec3 color;
                #endif
                
                varying vec3 vPos;
                varying vec3 vColor;
                void main() {
                    vPos = position;
                    vColor = color;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                varying vec3 vPos;
                varying vec3 vColor;
                uniform float size;
                uniform vec3 color1;
                uniform vec3 color2;
                uniform float time;
                uniform float pulseSpeed;
                
                void main() {
                    float dist = length(vPos);
                    float fade = 1.0 - smoothstep(0.0, size / 2.0, dist);

                    // Discard non-center (divider) lines
                    float isCenter = step(0.8, vColor.g);
                    if (isCenter < 0.5) discard;

                    float pulse = 0.5 + 0.5 * sin(time * pulseSpeed);
                    float pulseMod = mix(0.5, 1.0, pulse);

                    gl_FragColor = vec4(color1, fade * pulseMod);
                }
            `
        });

        this.#shaderMaterial = shaderMaterial;
        this.#gridHelper.material = shaderMaterial as any;
        this.#gridHelper.visible = false;
        this.#gridHelper.name = "PlaneGridHelper";

        // Circle: 50cm diameter = 250mm radius, lying in XZ plane
        const circleRadius = sceneUnitsFromMm(250);
        const circleSegments = 64;
        const circlePoints: THREE.Vector3[] = [];
        for (let i = 0; i <= circleSegments; i++) {
            const angle = (i / circleSegments) * Math.PI * 2;
            circlePoints.push(new THREE.Vector3(Math.cos(angle) * circleRadius, 0, Math.sin(angle) * circleRadius));
        }
        const circleGeometry = new THREE.BufferGeometry().setFromPoints(circlePoints);
        const circleMaterial = new THREE.ShaderMaterial({
            transparent: true,
            depthTest: true,
            blending: THREE.NormalBlending,
            uniforms: shaderMaterial.uniforms, // share uniforms so color & pulse stay in sync
            vertexShader: `
                void main() {
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform float time;
                uniform float pulseSpeed;
                uniform vec3 color1;
                void main() {
                    float pulse = 0.5 + 0.5 * sin(time * pulseSpeed);
                    gl_FragColor = vec4(color1, mix(0.5, 1.0, pulse));
                }
            `
        });
        this.#circleHelper = new THREE.LineLoop(circleGeometry, circleMaterial);
        this.#circleHelper.visible = false;
        this.#circleHelper.name = "PlaneGridCircle";
    }

    addToScene(scene: THREE.Scene) {
        scene.add(this.#gridHelper);
        scene.add(this.#circleHelper);
    }

    get activeNormal(): THREE.Vector3 | null {
        if (!this.#lastNormal) return null;
        if (this.#rotationState === 0) return this.#lastNormal.clone();

        const n = this.#lastNormal;
        const worldZ = new THREE.Vector3(0, 0, 1);

        // axis1: horizontal axis perpendicular to the surface normal.
        // When the normal is parallel to Z (floor/ceiling), fall back to the model X axis.
        let axis1: THREE.Vector3;
        const cross = new THREE.Vector3().crossVectors(n, worldZ);
        if (cross.lengthSq() < 0.001) {
            const rotationOffset = useModelPropertiesStore.getState().rotationOffset;
            axis1 = new THREE.Vector3(1, 0, 0).applyAxisAngle(worldZ, rotationOffset);
        } else {
            axis1 = cross.normalize();
        }

        if (this.#rotationState === 1) {
            return n.clone().applyAxisAngle(axis1, Math.PI / 2).normalize();
        } else {
            // axis2: perpendicular to both the normal and axis1
            const axis2 = new THREE.Vector3().crossVectors(n, axis1).normalize();
            return n.clone().applyAxisAngle(axis2, Math.PI / 2).normalize();
        }
    }

    cycleRotationOffset() {
        this.#rotationState = (this.#rotationState + 1) % 3;
        this.#updateUniforms();
        if (this.#lastPoint) this.update(this.#lastPoint, null);
    }

    resetRotationOffset() {
        this.#rotationState = 0;
        this.#updateUniforms();
    }

    #updateUniforms() {
        if (!this.#shaderMaterial.uniforms) return;
        
        if (this.#rotationState === 1) {
            this.#shaderMaterial.uniforms.color1.value = this.#lockedColor1;
            this.#shaderMaterial.uniforms.color2.value = this.#lockedColor2;
            this.#shaderMaterial.uniforms.pulseSpeed.value = 8.0;
        } else if (this.#rotationState === 2) {
            this.#shaderMaterial.uniforms.color1.value = this.#zOffsetColor1;
            this.#shaderMaterial.uniforms.color2.value = this.#zOffsetColor2;
            this.#shaderMaterial.uniforms.pulseSpeed.value = 8.0;
        } else {
            this.#shaderMaterial.uniforms.color1.value = this.#unlockedColor1;
            this.#shaderMaterial.uniforms.color2.value = this.#unlockedColor2;
            this.#shaderMaterial.uniforms.pulseSpeed.value = 4.0;
        }
    }

    update(point: THREE.Vector3, normal: THREE.Vector3 | null) {
        if (normal) {
            this.#lastNormal = normal.clone();
        }
        this.#lastPoint = point.clone();
        const activeNorm = this.activeNormal;
        const offset = activeNorm ? activeNorm.clone().multiplyScalar(sceneUnitsFromMm(1)) : new THREE.Vector3();
        this.#gridHelper.position.copy(point).add(offset);
        this.#circleHelper.position.copy(this.#gridHelper.position);

        if (!activeNorm) return;

        // GridHelper defaults to XZ plane, so its normal is Y axis (0, 1, 0)
        const defaultNormal = new THREE.Vector3(0, 1, 0);
        
        // Target orientation
        const targetQuaternion = new THREE.Quaternion().setFromUnitVectors(defaultNormal, activeNorm);

        // Orient grid in xy-plane
        if (Math.abs(activeNorm.z) > 0.5) {
            const rotationOffset = useModelPropertiesStore.getState().rotationOffset;
            const sign = activeNorm.z > 0 ? 1 : -1;
            const zRotation = new THREE.Quaternion().setFromAxisAngle(defaultNormal, sign * rotationOffset);
            targetQuaternion.multiply(zRotation);
        }

        this.#gridHelper.quaternion.copy(targetQuaternion);
        this.#circleHelper.quaternion.copy(targetQuaternion);
    }

    show() {
        if (useViewerStateStore.getState().viewerMode?.stageType === StageTypeEnum.Stage2d) {
            this.hide();
            return;
        }

        this.#enabled = true;
        this.#gridHelper.visible = true;
        this.#circleHelper.visible = true;
        this.#startAnimation();
    }

    hide() {
        this.#enabled = false;
        this.#gridHelper.visible = false;
        this.#circleHelper.visible = false;
        this.#stopAnimation();
    }
    
    #startAnimation = () => {
        if (!this.#enabled) return;
        
        this.#shaderMaterial.uniforms.time.value = performance.now() / 1000;
        
        this.#animationFrameId = requestAnimationFrame(this.#startAnimation);
    }

    #stopAnimation = () => {
        if (this.#animationFrameId !== null) {
            cancelAnimationFrame(this.#animationFrameId);
            this.#animationFrameId = null;
        }
    }

    dispose() {
        this.#stopAnimation();
        if (this.#gridHelper.parent) {
            this.#gridHelper.parent.remove(this.#gridHelper);
        }
        this.#gridHelper.geometry.dispose();
        (this.#gridHelper.material as THREE.Material).dispose();
        if (this.#circleHelper.parent) {
            this.#circleHelper.parent.remove(this.#circleHelper);
        }
        this.#circleHelper.geometry.dispose();
        (this.#circleHelper.material as THREE.Material).dispose();
    }
}
